/**
 * FlowsApiContext — the ONE seam that decides which backend the shared flow
 * builder pieces talk to.
 *
 * The visual builder (canvas + palette + inspector + dry-run panel) is reused
 * by TWO surfaces with different transports:
 *   • the PRODUCT editor (``/workspace/flows/$flowId``) → ``/api/v1/hive-flows``
 *     via ``@ando/core`` (bearer/product auth);
 *   • the PLATFORM CONSOLE editor (``/console/flows/$flowId``) →
 *     ``/platform/console/v1/flows`` (session-cookie auth, same ``FlowService``
 *     behind, identical wire shapes).
 *
 * The context's DEFAULT VALUE is the product implementation, so the product
 * builder needs NO provider and keeps behaving exactly as before (shared
 * hot-path — see the "never break as-is" rule). The console wraps its pages in
 * ``<FlowsApiProvider api={consoleFlowsApi}>`` (``features/console/flows``).
 *
 * Only what SHARED components actually call goes through the seam: the node
 * palette, the on-demand connector schema, and the pending-approvals
 * read/approve pair. Flow CRUD / run stays with each surface's own route glue
 * (the product route calls ``@ando/core`` directly, the console page calls its
 * console client) — parametrizing those here would widen the seam for nothing.
 *
 * Query keys are scoped by ``FlowsApi.scope`` (``flowsApiQueryKeys``) so the
 * two surfaces never share a TanStack cache entry (different auth, possibly
 * different visibility).
 */

import {
  type ArticleGroupCatalog,
  type ApproveRunEdits,
  type BancoLockResult,
  type BancoRoomSnapshot,
  type CommissionLegacyImportResult,
  type CommissionStatement,
  type CommissionStatementDetail,
  type CommissionStatementTransition,
  type CommissionStatementsResponse,
  type FlowRun,
  type FlowRunDetailResponse,
  type FlowRunListResponse,
  type NodeSchemaResponse,
  type PaletteResponse,
  type PendingApproval,
  type ReconciliationDecisionRequest,
  type ReconciliationDecisionResult,
  type StructuredDraft,
  type StructuredDraftArtifact,
  type StructuredDraftArtifactUrl,
  type StructuredDraftHistory,
  type SupportCaseReplyResult,
  type SupportCaseState,
  type SupportCaseStatus,
  approveFlowRun,
  bancoHeartbeat,
  bancoJoin,
  bancoLeave,
  bancoLock,
  bancoUnlock,
  createCommissionStatementsFromRun,
  createStructuredDraft,
  createStructuredDraftArtifact,
  decideReconciliationItem,
  exportCommissionStatementXlsx,
  exportFlowRunXlsx,
  getCommissionStatement,
  getFlowPalette,
  getFlowArticleGroups,
  getFlowRun,
  getNodeConnectorSchema,
  getPendingApprovals,
  getStructuredDraft,
  getStructuredDraftArtifactUrl,
  getStructuredDraftHistory,
  importCommissionLegacyHistory,
  listCommissionStatements,
  listFlowRuns,
  patchStructuredDraft,
  rejectFlowRun,
  requestCommissionInvoice,
  runFlow,
  runFlowWithFile,
  sendCommissionInvoice,
  sendFlowSupportCaseReply,
  setFlowSupportCaseStatus,
  testFlowNode,
  transitionCommissionStatement,
} from "@ando/core";
import * as React from "react";

export interface NodeSchemaParams {
  readonly provider?: string;
  readonly objectType?: string;
  readonly subAction?: string;
}

export interface FlowsApi {
  /** Cache scope segment — keeps product/console TanStack caches separate. */
  readonly scope: string;
  readonly getPalette: () => Promise<PaletteResponse>;
  readonly getArticleGroups?: (flowId: string) => Promise<ArticleGroupCatalog>;
  readonly getNodeSchema: (
    nodeType: string,
    params: NodeSchemaParams,
  ) => Promise<NodeSchemaResponse>;
  readonly getPendingApprovals: () => Promise<{ approvals: PendingApproval[] }>;
  /** Bare string keeps the legacy single ``editedValue`` call (#930); the
   * structured form adds ``editedFields`` (G-2) — both transports forward it. */
  readonly approveRun: (
    runId: string,
    edits?: string | ApproveRunEdits,
  ) => Promise<{ ok: boolean; status: string }>;
  /** The HITL "no" — terminal reject, the run never resumes. */
  readonly rejectRun: (runId: string, reason?: string) => Promise<{ ok: boolean; status: string }>;
  // ── Run lifecycle (widened Aug 2026) — the operator run-surface is now
  // shared by BOTH product (/workspace) and console (/console) too, so its
  // three data calls go through the seam like the builder pieces above. The
  // product default below preserves the exact previous @ando/core behaviour.
  /** Start a run WITHOUT a document — for flows whose trigger needs no file
   * input (manual-no-doc, scheduled, webhook, …). Creates + starts the run. */
  readonly run: (flowId: string, triggerData?: Record<string, unknown>) => Promise<FlowRun>;
  /** Upload a document as the run trigger (multipart) → creates + starts the run. */
  readonly runWithFile: (
    flowId: string,
    file: File | readonly File[],
    triggerData?: Record<string, unknown>,
  ) => Promise<FlowRun>;
  /** Mandatory dry-run of one node plus its upstream dependency closure. */
  readonly testNode?: (
    flowId: string,
    nodeId: string,
    triggerData?: Record<string, unknown>,
  ) => Promise<FlowRun>;
  /** Poll a single run's full projection (review re-hydration). */
  readonly getRun: (runId: string) => Promise<FlowRunDetailResponse>;
  readonly setSupportCaseStatus?: (
    runId: string,
    status: SupportCaseStatus,
  ) => Promise<SupportCaseState>;
  readonly sendSupportCaseReply?: (runId: string, body: string) => Promise<SupportCaseReplyResult>;
  /** Run history for the flow (most recent first, dry-runs excluded). */
  readonly listRuns: (flowId: string) => Promise<FlowRunListResponse>;
  readonly exportRunXlsx: (runId: string) => Promise<Blob>;
  readonly listCommissionStatements?: (params?: {
    periodStart?: string;
    periodEnd?: string;
    sellerKey?: string;
  }) => Promise<CommissionStatementsResponse>;
  readonly getCommissionStatement?: (statementId: string) => Promise<CommissionStatementDetail>;
  readonly createCommissionStatementsFromRun?: (
    runId: string,
    payload: {
      period_start: string;
      period_end: string;
      calculation_version: string;
      source_connection_key?: string;
      source_company?: string;
      currency?: string;
    },
  ) => Promise<CommissionStatementsResponse>;
  readonly importCommissionLegacyHistory?: (
    runId: string,
    file: File,
  ) => Promise<CommissionLegacyImportResult>;
  readonly transitionCommissionStatement?: (
    statementId: string,
    payload: CommissionStatementTransition,
  ) => Promise<{ statement: CommissionStatement }>;
  readonly requestCommissionInvoice?: (
    statementId: string,
    payload: { expected_version: number; idempotency_key: string; recipient?: string },
  ) => Promise<{ statement: CommissionStatement; delivery?: Record<string, unknown> | null }>;
  readonly sendCommissionInvoice?: typeof sendCommissionInvoice;
  readonly exportCommissionStatementXlsx?: (statementId: string) => Promise<Blob>;
  readonly decideReconciliationItem?: (
    runId: string,
    itemId: string,
    decision: ReconciliationDecisionRequest,
  ) => Promise<ReconciliationDecisionResult>;
  readonly structuredDrafts?: {
    create: (input: {
      run_id?: string;
      kind: string;
      schema_version: number;
      content: Record<string, unknown>;
    }) => Promise<StructuredDraft>;
    get: (draftId: string) => Promise<StructuredDraft>;
    patch: (
      draftId: string,
      version: number,
      operations: Array<{ op: "add" | "replace" | "remove"; path: string; value?: unknown }>,
    ) => Promise<StructuredDraft>;
    history: (draftId: string) => Promise<StructuredDraftHistory>;
    createArtifact: (draftId: string, format?: string) => Promise<StructuredDraftArtifact>;
    artifactUrl: (artifactId: string) => Promise<StructuredDraftArtifactUrl>;
  };
  // ── Banco room (real-time collab on a paused run) — commands only; events
  // ride the existing per-user WS. OPTIONAL: a transport that doesn't serve the
  // room routes (today: console) simply omits them and the workbench degrades
  // to no-presence/no-locks. The product default implements them all. ──
  readonly bancoJoin?: (runId: string) => Promise<BancoRoomSnapshot>;
  readonly bancoLeave?: (runId: string) => Promise<{ ok: boolean }>;
  readonly bancoHeartbeat?: (runId: string, fields: string[]) => Promise<{ ok: boolean }>;
  readonly bancoLock?: (runId: string, field: string) => Promise<BancoLockResult>;
  readonly bancoUnlock?: (runId: string, field: string) => Promise<{ ok: boolean }>;
}

/** The product transport — the context DEFAULT (no provider required). */
export const PRODUCT_FLOWS_API: FlowsApi = {
  scope: "product",
  getPalette: () => getFlowPalette(),
  getArticleGroups: (flowId) => getFlowArticleGroups(flowId),
  getNodeSchema: (nodeType, params) => getNodeConnectorSchema(nodeType, params),
  getPendingApprovals: () => getPendingApprovals(),
  approveRun: (runId, edits) => approveFlowRun(runId, edits),
  rejectRun: (runId, reason) => rejectFlowRun(runId, reason),
  run: (flowId, triggerData) => runFlow(flowId, { trigger_data: triggerData }),
  testNode: (flowId, nodeId, triggerData) => testFlowNode(flowId, nodeId, triggerData),
  runWithFile: (flowId, file, triggerData) => runFlowWithFile(flowId, file, { triggerData }),
  getRun: (runId) => getFlowRun(runId),
  setSupportCaseStatus: (runId, status) => setFlowSupportCaseStatus(runId, status),
  sendSupportCaseReply: (runId, body) => sendFlowSupportCaseReply(runId, body),
  listRuns: (flowId) => listFlowRuns(flowId),
  exportRunXlsx: (runId) => exportFlowRunXlsx(runId),
  listCommissionStatements: (params) => listCommissionStatements(params),
  getCommissionStatement: (statementId) => getCommissionStatement(statementId),
  createCommissionStatementsFromRun: (runId, payload) =>
    createCommissionStatementsFromRun(runId, payload),
  importCommissionLegacyHistory: (runId, file) => importCommissionLegacyHistory(runId, file),
  transitionCommissionStatement: (statementId, payload) =>
    transitionCommissionStatement(statementId, payload),
  requestCommissionInvoice: (statementId, payload) =>
    requestCommissionInvoice(statementId, payload),
  sendCommissionInvoice: (statementId, payload) => sendCommissionInvoice(statementId, payload),
  exportCommissionStatementXlsx: (statementId) => exportCommissionStatementXlsx(statementId),
  decideReconciliationItem: (runId, itemId, decision) =>
    decideReconciliationItem(runId, itemId, decision),
  structuredDrafts: {
    create: (input) => createStructuredDraft(input),
    get: (draftId) => getStructuredDraft(draftId),
    patch: (draftId, version, operations) => patchStructuredDraft(draftId, version, operations),
    history: (draftId) => getStructuredDraftHistory(draftId),
    createArtifact: (draftId, format) => createStructuredDraftArtifact(draftId, format),
    artifactUrl: (artifactId) => getStructuredDraftArtifactUrl(artifactId),
  },
  bancoJoin: (runId) => bancoJoin(runId),
  bancoLeave: (runId) => bancoLeave(runId),
  bancoHeartbeat: (runId, fields) => bancoHeartbeat(runId, fields),
  bancoLock: (runId, field) => bancoLock(runId, field),
  bancoUnlock: (runId, field) => bancoUnlock(runId, field),
};

const FlowsApiContext = React.createContext<FlowsApi>(PRODUCT_FLOWS_API);

export function FlowsApiProvider({
  api,
  children,
}: {
  api: FlowsApi;
  children: React.ReactNode;
}): React.JSX.Element {
  return <FlowsApiContext.Provider value={api}>{children}</FlowsApiContext.Provider>;
}

export function useFlowsApi(): FlowsApi {
  return React.useContext(FlowsApiContext);
}

/** Scope-aware query keys for everything the seam serves. */
export const flowsApiQueryKeys = {
  palette: (scope: string) => ["hive-flows", scope, "palette"] as const,
  nodeSchema: (scope: string, nodeType: string, params: NodeSchemaParams) =>
    [
      "hive-flows",
      scope,
      "node-schema",
      nodeType,
      params.provider ?? "",
      params.objectType ?? "",
      params.subAction ?? "",
    ] as const,
  pendingApprovals: (scope: string) => ["hive-flows", scope, "pending-approvals"] as const,
  /** A single run's polled detail — scoped so product/console never collide. */
  run: (scope: string, runId: string) => ["hive-flows", scope, "run", runId] as const,
  /** A flow's run history list. */
  runs: (scope: string, flowId: string) => ["hive-flows", scope, "runs", flowId] as const,
};
